import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { User, LogOut, Calendar, CalendarDays, LayoutDashboard, Menu, X, Settings, Trophy } from 'lucide-react';

export const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-stone-200 glass-effect">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-10 h-10 bg-primary rounded-md flex items-center justify-center">
              <span className="text-white font-display text-xl font-bold">M</span>
            </div>
            <span className="font-display text-xl font-semibold text-stone-900 group-hover:text-primary transition-colors">
              Manitoulin HEC
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <Link
              to="/events"
              className="text-stone-600 hover:text-primary transition-colors font-medium"
              data-testid="nav-events"
            >
              Events
            </Link>
            <Link
              to="/calendar"
              className="text-stone-600 hover:text-primary transition-colors font-medium"
              data-testid="nav-calendar"
            >
              Calendar
            </Link>
            <Link
              to="/results"
              className="text-stone-600 hover:text-primary transition-colors font-medium"
              data-testid="nav-results"
            >
              Results
            </Link>
            <Link
              to="/standings"
              className="text-stone-600 hover:text-primary transition-colors font-medium"
              data-testid="nav-standings"
            >
              Standings
            </Link>
            <Link
              to="/rules"
              className="text-stone-600 hover:text-primary transition-colors font-medium"
              data-testid="nav-rules"
            >
              Rules
            </Link>
            {user && (
              <Link
                to="/my-registrations"
                className="text-stone-600 hover:text-primary transition-colors font-medium"
                data-testid="nav-my-registrations"
              >
                My Registrations
              </Link>
            )}
            {(user?.role === 'committee' || user?.role === 'president') && (
              <Link
                to="/dashboard"
                className="text-stone-600 hover:text-primary transition-colors font-medium"
                data-testid="nav-dashboard"
              >
                Dashboard
              </Link>
            )}
          </nav>

          {/* Auth Section */}
          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="gap-2" data-testid="user-menu-btn">
                    {user.profile_picture ? (
                      <img 
                        src={user.profile_picture} 
                        alt="" 
                        className="w-6 h-6 rounded-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <User className="w-4 h-4" />
                    )}
                    <span className="max-w-32 truncate">{user.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="menu-profile">
                    <Settings className="w-4 h-4 mr-2" />
                    Account Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/calendar')} data-testid="menu-calendar">
                    <CalendarDays className="w-4 h-4 mr-2" />
                    Club Calendar
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/results')} data-testid="menu-results">
                    <Trophy className="w-4 h-4 mr-2" />
                    Results & Standings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/my-registrations')} data-testid="menu-my-registrations">
                    <Calendar className="w-4 h-4 mr-2" />
                    My Registrations
                  </DropdownMenuItem>
                  {(user.role === 'committee' || user.role === 'president') && (
                    <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      Dashboard
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate('/login')} data-testid="login-btn">
                  Log In
                </Button>
                <Button onClick={() => navigate('/register')} className="bg-primary hover:bg-primary/90" data-testid="register-btn">
                  Join Now
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-stone-200">
            <nav className="flex flex-col gap-4">
              <Link
                to="/events"
                className="text-stone-600 hover:text-primary transition-colors font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Events
              </Link>
              <Link
                to="/calendar"
                className="text-stone-600 hover:text-primary transition-colors font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Calendar
              </Link>
              <Link
                to="/results"
                className="text-stone-600 hover:text-primary transition-colors font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Results
              </Link>
              <Link
                to="/standings"
                className="text-stone-600 hover:text-primary transition-colors font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Standings
              </Link>
              <Link
                to="/rules"
                className="text-stone-600 hover:text-primary transition-colors font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Rules
              </Link>
              {user && (
                <Link
                  to="/my-registrations"
                  className="text-stone-600 hover:text-primary transition-colors font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  My Registrations
                </Link>
              )}
              {(user?.role === 'committee' || user?.role === 'president') && (
                <Link
                  to="/dashboard"
                  className="text-stone-600 hover:text-primary transition-colors font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Dashboard
                </Link>
              )}
              <div className="flex gap-2 pt-4 border-t border-stone-200">
                {user ? (
                  <Button onClick={handleLogout} variant="outline" className="w-full">
                    Logout
                  </Button>
                ) : (
                  <>
                    <Button variant="outline" onClick={() => { navigate('/login'); setMobileMenuOpen(false); }} className="flex-1">
                      Log In
                    </Button>
                    <Button onClick={() => { navigate('/register'); setMobileMenuOpen(false); }} className="flex-1 bg-primary">
                      Join Now
                    </Button>
                  </>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-stone-900 text-white py-16">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 bg-primary rounded-md flex items-center justify-center">
                <span className="text-white font-display text-xl font-bold">M</span>
              </div>
              <span className="font-display text-xl font-semibold">Manitoulin HEC</span>
            </div>
            <p className="text-stone-400 max-w-sm">
              Your premier destination for horse shows, clinics, and equestrian events. 
              Join our Manitoulin community of passionate riders.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-display text-lg font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/events" className="text-stone-400 hover:text-white transition-colors">
                  Events
                </Link>
              </li>
              <li>
                <Link to="/calendar" className="text-stone-400 hover:text-white transition-colors">
                  Calendar
                </Link>
              </li>
              <li>
                <Link to="/results" className="text-stone-400 hover:text-white transition-colors">
                  Results & Standings
                </Link>
              </li>
              <li>
                <Link to="/standings" className="text-stone-400 hover:text-white transition-colors">
                  Season Standings
                </Link>
              </li>
              <li>
                <Link to="/rules" className="text-stone-400 hover:text-white transition-colors">
                  Club Rules
                </Link>
              </li>
              <li>
                <Link to="/register" className="text-stone-400 hover:text-white transition-colors">
                  Join Club
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-display text-lg font-semibold mb-4">Contact</h4>
            <ul className="space-y-2 text-stone-400">
              <li>info@equiclub.com</li>
              <li>(555) 123-4567</li>
            </ul>
          </div>
        </div>

        <div className="border-t border-stone-800 mt-12 pt-8 text-center text-stone-500">
          <p>&copy; {new Date().getFullYear()} Manitoulin Horse Enthusiasts Club. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
};
